import React, { ChangeEvent, FormEvent, useEffect, useState } from 'react';
import { BaseBookReturn } from '../../../generated/client/models/BaseBookReturn';
import { ResultsComponent } from './ResultsComponent';
import './Library.css';
import { BookServiceFactory } from '../BookServiceFactory';

/**
 * The BookSearchBar does the following:
 * -- queries database to retrieve list of books based on search terms
 * -- handles filters of search terms
 *
 */

export function BookSearchBar(): JSX.Element {
  const gutendexBooksPerPage = 32; // default number of results per page specified by API
  const booksService = BookServiceFactory.instance();
  const genreOrKeyword = 'Genre/Keyword';
  const titleOrAuthor = 'Title/Author';
  const [bookData, setBookData] = useState<BaseBookReturn[]>([]); // arrary of books
  const [keyword, setKeyword] = useState<string>(''); // string to be used to call listBooks
  const [numPages, setNumPages] = useState<number[]>([]); // number of results pages resulting from call to listBooks with keyword
  const [bookCount, setBookCount] = useState<number>(); // number of books resulting from call to listBooks with keyword
  const filterOptions = [titleOrAuthor, genreOrKeyword]; // options to filter search by
  const [searchFilter, setSearchFilter] = useState<string>(titleOrAuthor); // current search filter
  const [pageFilter, setPageFilter] = useState<number>(1); // current page filter

  // transforms number of pages into array listing each number
  function getPageArray(pages: number): number[] {
    const pageNumArray: number[] = [];
    for (let i = 1; i <= pages; i++) {
      pageNumArray.push(i);
    }
    return pageNumArray;
  }

  // retrieves lower bound of resulting books from search on current page
  function getResultLowerBoundNumber(page: number): number {
    return gutendexBooksPerPage * (page - 1) + 1;
  }

  // retrieves upper bound of resulting books from search on current page
  function getResultUpperBoundNumber(page: number): number | undefined {
    if (page == numPages.length) {
      return bookCount;
    } else {
      return gutendexBooksPerPage * page;
    }
  }

  // handles event when user clicks the search button to search for an audiobook
  const search = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.target as HTMLFormElement;
    const input = form.querySelector('#searchText') as HTMLInputElement;
    setBookData([]); // clears data from previous search
    setKeyword(input.value); // sets keyword as user input
    setPageFilter(1); // resets current page number to 1
  };

  // handles event when user clicks the 'browse all' button
  const browseAll = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const input = ' ';
    setKeyword(input); // sets keyword as empty string so all books are queried
    setBookData([]); // clears data from previous search
    setPageFilter(1); // resets current page number to 1
  };

  // handles event when user changes the filter of a book search
  const handleFilter = (event: ChangeEvent<HTMLSelectElement>) => {
    event.preventDefault();
    setSearchFilter(event.target.value); // sets new filter
  };

  // handled event when use changes page number filter
  const handlePageFilter = (event: ChangeEvent<HTMLSelectElement>) => {
    event.preventDefault();
    setPageFilter(parseInt(event.target.value)); // sets current page number
    setBookData([]); // clears data from previous search
  };

  // query to retrieve list of books each time the search term, filter, or page number changes
  useEffect(() => {
    (async () => {
      const query = keyword; // most recent user input
      if (query) {
        let response = await booksService.listBooks(undefined, undefined, undefined, pageFilter);

        if (searchFilter == titleOrAuthor) {
          response = await booksService.listBooks(undefined, query, undefined, pageFilter);
        } // search by title or author

        if (searchFilter == genreOrKeyword) {
          response = await booksService.listBooks(query, undefined, undefined, pageFilter);
        } // search by genre or keyword

        setBookData(response.books); // sets bookdata based on filter, keyword, and page number
        setNumPages(getPageArray(response.pageCount)); // sets numer of results pages based on query
        setBookCount(response.bookCount); // sets total number of books retrived in query
      }
    })();
  }, [keyword, searchFilter, pageFilter, booksService]);

  return (
    <div className='wrap'>
      <div className='searchBar'>
        <select id='dropdown' className='dropDown' onChange={handleFilter}>
          {filterOptions.map(opt => (
            <option key={opt}> {opt} </option>
          ))}
          ;
        </select>
        <form className='searchForm' onSubmit={event => search(event)}>
          <input id='searchText' type='text' className='searchTerm' />
          <button className='searchButton'>Search</button>
        </form>
        <form className='browseAllForm' onSubmit={event => browseAll(event)}>
          <button className='browseAllButton'>Browse All</button>
        </form>
      </div>
      <div className='resultsContainer'>
        {keyword && (
          <div className='resultsDescription'>
            <b>
              {' '}
              {bookData.length != 0
                ? `Showing ${getResultLowerBoundNumber(pageFilter)} - ${getResultUpperBoundNumber(
                    pageFilter,
                  )}  Results for ${keyword} ...`
                : `Searching for ${keyword} ...`}
            </b>
            <div>
              <b> Page: </b>
              <select
                id='pageDropdown'
                className='dropDown'
                onChange={handlePageFilter}
                value={pageFilter}>
                {numPages.map(page => (
                  <option key={page} value={page}>
                    {page}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}

        {bookData.map(book => (
          <ResultsComponent key={book.id} book={book} bookService={booksService}></ResultsComponent>
        ))}
      </div>
    </div>
  );
}
